import type { ReactNode } from 'react';
import type { StorageResourceRef } from '../../../../types/azureStorage';
import type { StorageExplorerMode } from '../../core/types/workspaceTypes';

export type StorageToolbarContext = {
  connectionId: string;
  root: StorageResourceRef;
  resource: StorageResourceRef;
  mode: StorageExplorerMode;
  selection: StorageResourceRef[];
  readOnly: boolean;
  capabilities: Readonly<Record<string, boolean | string | undefined>>;
  openDialog: (dialog: ReactNode) => void;
  refresh: () => void;
};

export type StorageToolbarExtension = {
  id: string;
  /** `more`: `render` returns a `StorageToolbarMenuItem` (a real Radix menu item — UI/UX fix round A, review A4). */
  placement: 'primary' | 'more';
  order: number;
  /** A `more` entry that deletes: listed last, after a separator (review U16). */
  destructive?: boolean;
  isVisible(ctx: StorageToolbarContext): boolean;
  render(ctx: StorageToolbarContext): ReactNode;
  /**
   * A `primary` entry that may move into the More menu when the action row runs out of width (final UI/UX batch C1,
   * R5 / N2): lower `priority` moves first; `renderMenuItem` returns its `StorageToolbarMenuItem` form (same action,
   * same reason). An entry without it always stays in the row (the primary operation: Upload).
   */
  overflow?: { priority: number; renderMenuItem(ctx: StorageToolbarContext): ReactNode };
};

/** Overflow priorities of the action row (higher stays longer); Refresh, copy / export and the primary operation never move. */
export const TOOLBAR_OVERFLOW_PRIORITY = { download: 60, preview: 55, newFolder: 50, properties: 40, downloadFolder: 30, snapshots: 20 } as const;

const extensions = new Map<string, StorageToolbarExtension>();

export function registerStorageToolbarExtension(extension: StorageToolbarExtension): () => void {
  extensions.set(extension.id, extension);
  return () => {
    if (extensions.get(extension.id) === extension) extensions.delete(extension.id);
  };
}

export function getVisibleStorageToolbarExtensions(ctx: StorageToolbarContext): StorageToolbarExtension[] {
  return [...extensions.values()]
    .filter((extension) => extension.isVisible(ctx))
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));
}
